import React from 'react';
import { Button, Card, Col, Divider, Row, Table, TableColumnsType, Tag, Tooltip, Typography } from 'antd';
import { textNumberFormater } from '../../../../utils';
import { TReceiptData } from '../../../../models/salesModel';

const { Text } = Typography;

interface BillDetailItemsCardProps {
  title: string;
  isSaleInvoice: boolean;
  referenceDocument?: string | null;
  referenceDocumentUid?: string | null;
  onNavigateOrigin?: (uid: string) => void;
  itemListData: any[];
  hasPendingFulfillment: boolean;
  documentData: TReceiptData;
}

export const BillDetailItemsCard: React.FC<BillDetailItemsCardProps> = ({
  title,
  isSaleInvoice,
  referenceDocument,
  referenceDocumentUid,
  onNavigateOrigin,
  itemListData,
  hasPendingFulfillment,
  documentData
}) => {
  const columnsItems: TableColumnsType = [
    { title: "Articulo", dataIndex: "custom_id", key: "custom_id", render: (text, record: any) => record.item_code || text },
    { title: "Nombre", dataIndex: "name", key: "name", render: (text, record: any) => {
      const name = record.item_name || text;
      const regType = record.register_type;
      return (
        <span>
          {name}
          {regType === 'SERVICE' && <Tag color="geekblue" style={{ marginLeft: 6, fontSize: 10 }}>Servicio</Tag>}
          {regType === 'FIXED_ASSET' && <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>Activo Fijo</Tag>}
        </span>
      );
    }},
    { title: "Cantidad", dataIndex: "quantity", key: "quantity", align: "center" },
    ...(hasPendingFulfillment ? [{
      title: "Despachado",
      key: "quantity_received",
      align: "center" as const,
      width: 140,
      render: (_: any, record: any) => {
        if (record.register_type === 'SERVICE' || record.track_inventory === false) {
          return <Tag color="default">N/A</Tag>;
        }
        const received = Number(record.quantity_received || 0);
        const total = Number(record.quantity || 0);
        const isComplete = received >= total;
        return (
          <Tag color={isComplete ? "success" : received > 0 ? "warning" : "volcano"}>
            {received} / {total} {record.display || record.presentation || "UND"}
          </Tag>
        );
      }
    }] : []),
    { title: "Precio", dataIndex: "client_unit_price", key: "client_unit_price", align: "right", render: (text) => textNumberFormater(text) },
    { title: "Descuento", dataIndex: "discount_amount", key: "discount_amount", align: "right", render: (text) => textNumberFormater(text) },
    { 
      title: "Impuesto", 
      dataIndex: "total_tax_amount", 
      key: "total_tax_amount", 
      align: "right", 
      render: (text: any, record: any) => {
        const taxes = record.taxes || record.taxes_list;
        const hasMultipleTaxes = Array.isArray(taxes) && taxes.length > 1;
        const formatted = textNumberFormater(text);
        if (!hasMultipleTaxes) {
          return formatted;
        }
        const tooltipContent = (
          <div style={{ textAlign: 'left' }}>
            {taxes.map((t: any, idx: number) => (
              <div key={idx}>
                {t.description || t.key || 'Impuesto'}: {textNumberFormater(t.tax_amount)}
              </div>
            ))}
          </div>
        );
        return (
          <Tooltip title={tooltipContent}>
            <span style={{ borderBottom: '1px dotted #8c8c8c', cursor: 'help' }}>
              {formatted}
            </span>
          </Tooltip>
        );
      }
    },
    { title: "Total", dataIndex: "client_amount", key: "client_amount", align: "right", render: (text) => textNumberFormater(text) }
  ];

  return (
    <Card 
      title={`Ítems ${title}`} 
      className="card-shadow" 
      extra={
        !isSaleInvoice && referenceDocumentUid && onNavigateOrigin && (
          <Button type="link" onClick={() => onNavigateOrigin(referenceDocumentUid)}>
            {`Origen: ${referenceDocument}`}
          </Button>
        )
      }
    >
      <Table 
        columns={columnsItems} 
        dataSource={itemListData} 
        pagination={false} 
        size="small"
        rowKey={(record: any, index?: number) => String(record.uid || record.id || record.custom_id || record.key || record.element_id || `item-${index}`)}
        scroll={{ x: 600 }}
      />
      <Divider />
      <Row justify="end">
        <Col xs={24} sm={12} md={10}>
          <div style={{ textAlign: 'right' }}>
            <p><Text strong>Subtotal:</Text> {textNumberFormater(documentData.subtotal)}</p>
            <p><Text strong>Descuentos:</Text> {textNumberFormater(documentData.totalDiscount)}</p>
            <p><Text strong>Impuestos:</Text> {textNumberFormater(documentData.tax)}</p>
            <p><Text strong style={{ fontSize: 18 }}>TOTAL:</Text> <Text strong style={{ fontSize: 18, color: '#1890ff' }}>{textNumberFormater(documentData.total)}</Text></p>
          </div>
        </Col>
      </Row>
    </Card>
  );
};
